// biome-ignore lint/style/noRestrictedImports: legacy suppression
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { NotFound } from "src/pages/not-found";
import { Unauthorized } from "src/pages/unauthorized";
import { Warning } from "src/components/warning";
import { PageSpinner } from "src/components/loading";
import { captureException, isChunkLoadError } from "src/lib/monitoring";
import { reloadForChunkError, reloadForDeploySkew } from "src/lib/chunk-reload";
import { isDeploySkewError } from "src/lib/chunk-retry";

export const RouteErrorBoundary = () => {
  const error = useRouteError();

  // Deploy skew (a chunk 404 / SPA-HTML at an asset path) takes a single
  // URL-preserving reload to the fresh build, off the poison budget and
  // guarded to once per build. Check it before the generic chunk path since a
  // skew error also matches isChunkLoadError.
  if (isDeploySkewError(error)) {
    if (reloadForDeploySkew(error)) {
      return <PageSpinner />;
    }
    captureException(error);
    // Already reloaded once for this build; fall through to show error UI.
  } else if (isChunkLoadError(error)) {
    if (reloadForChunkError(error)) {
      // The reload may be deferred a few seconds by the pre-reload cache
      // overwrite — show a spinner rather than a blank pane meanwhile.
      return <PageSpinner />;
    }
    captureException(error);
    // Fall through to show error UI if reload already attempted
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <NotFound />;
    }
    if (error.status === 401) {
      return <Unauthorized />;
    }
  }

  return (
    <Warning
      variant="error"
      title="An error has occurred"
      subtitle="We have been alerted and are looking into it. Please try again later."
    />
  );
};
